import { faTrashAlt } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AmortizacionReadingDtoProps } from "api/DTOs/AmortizacionReadingDto"
import { TIPO_COMISION } from "api/Types/TipoComision"
import { isAnnotation, isExpansionLoan } from "common/utils/utils"
import { IconAnnotationTemplate } from "features/Documentos/templates"
import { TFunction } from "i18next"
import { InputSwitch } from "primereact/inputswitch"
import { frontendDateFormatWhitoutDay } from "utils/formatDate"
import { precisionNumber } from "utils/precisionNumber"
import { TYPE_DELETE_PRESTAMOS } from "../../../constants"
import { DeleteModalState } from "../constants"

const isDatePastOrToday = (rowData: AmortizacionReadingDtoProps): boolean => {
  const dateToCheck = new Date(rowData.fecha)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return dateToCheck <= today && rowData.asientoContable === null
}

export const PRESTAMO_DATAIL_COLUMNS = (
  t: TFunction,
  amortizacionesPrestamosById: AmortizacionReadingDtoProps[],
  actions: {
    handleOpenAnnotationModal: (rowData: AmortizacionReadingDtoProps) => void
    handleEditAmortizacion: (amortizacionId: string, isChecked: boolean) => void
    handleOpenModalPrestamo: (data: AmortizacionReadingDtoProps) => void
    setShowDelete: React.Dispatch<React.SetStateAction<DeleteModalState>>
  }
) => {
  const formatCurrency = (value: number) =>
    `${new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(precisionNumber(value ?? 0))} €`

  const columns = [
    {
      key: "Fecha",
      header: t("prestamos.idView.tab.headerPeriod"),
      field: "fecha",
      alignCol: "center",
      className: "!tw-text-center !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => {
        return (
          <span
            className={`tw-py-3 tw-pl-10 tw-pr-3 !tw-h-full tw-flex tw-items-center tw-justify-center
                      ${
                        rowData.asientoContable
                          ? "tw-text-info hover:tw-underline tw-cursor-pointer"
                          : ""
                      }
                      ${isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""}`}
            onClick={() => actions.handleOpenModalPrestamo(rowData)}
          >
            {frontendDateFormatWhitoutDay(new Date(rowData.fecha))}
          </span>
        )
      },
    },
    {
      key: "cuota",
      header: t("prestamos.idView.tab.headerQuota"),
      field: "cuota",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => {
        return (
          <span
            className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
              isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
            }`}
          >
            {formatCurrency(rowData.cuota)}
          </span>
        )
      },
    },
    {
      key: "comisionesApertura",
      header: t("prestamos.idView.tab.headerOpenCom"),
      field: "comisiones",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(
            rowData.comisiones?.find(
              (value) => value.tipoComision === TIPO_COMISION.APERTURA
            )?.valor ?? 0
          )}
        </span>
      ),
      shouldRender: amortizacionesPrestamosById?.some(
        (amortizacion: AmortizacionReadingDtoProps) =>
          amortizacion?.comisiones?.some(
            (comision) => comision.tipoComision === TIPO_COMISION.APERTURA
          )
      ),
    },
    {
      key: "comisionesEstudio",
      header: t("prestamos.idView.tab.headerOpenSta"),
      field: "comisiones",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(
            rowData.comisiones?.find(
              (value) => value.tipoComision === TIPO_COMISION.ESTUDIO
            )?.valor ?? 0
          )}
        </span>
      ),
      shouldRender: amortizacionesPrestamosById?.some(
        (amortizacion: AmortizacionReadingDtoProps) =>
          amortizacion?.comisiones?.some(
            (comision) => comision.tipoComision === TIPO_COMISION.ESTUDIO
          )
      ),
    },
    {
      key: "comisionesDisposicion",
      header: t("prestamos.idView.tab.headerOpenDep"),
      field: "comisiones",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(
            rowData.comisiones?.find(
              (value) => value.tipoComision === TIPO_COMISION.DISPOSICION
            )?.valor ?? 0
          )}
        </span>
      ),
      shouldRender: amortizacionesPrestamosById?.some(
        (amortizacion: AmortizacionReadingDtoProps) =>
          amortizacion?.comisiones?.some(
            (comision) => comision.tipoComision === TIPO_COMISION.DISPOSICION
          )
      ),
    },
    {
      key: "comisionesReembolso",
      header: t("prestamos.idView.tab.headerOpenRem"),
      field: "comisiones",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10 " : ""
          }`}
        >
          {formatCurrency(
            rowData.comisiones?.find(
              (value) => value.tipoComision === TIPO_COMISION.REEMBOLSO
            )?.valor ?? 0
          )}
        </span>
      ),
      shouldRender: amortizacionesPrestamosById?.some(
        (amortizacion: AmortizacionReadingDtoProps) =>
          amortizacion?.comisiones?.some(
            (comision) => comision.tipoComision === TIPO_COMISION.REEMBOLSO
          )
      ),
    },
    {
      key: "comisionesCorretaje",
      header: t("prestamos.idView.tab.headerOpenCorr"),
      field: "comisiones",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(
            rowData.comisiones?.find(
              (value) => value.tipoComision === TIPO_COMISION.CORRETAJE
            )?.valor ?? 0
          )}
        </span>
      ),
      shouldRender: amortizacionesPrestamosById?.some(
        (amortizacion: AmortizacionReadingDtoProps) =>
          amortizacion?.comisiones?.some(
            (comision) => comision.tipoComision === TIPO_COMISION.CORRETAJE
          )
      ),
    },
    {
      key: "comisionesOtra",
      header: t("prestamos.idView.tab.headerOther"),
      field: "comisiones",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(
            rowData.comisiones?.find(
              (value) => value.tipoComision === TIPO_COMISION.OTRA
            )?.valor ?? 0
          )}
        </span>
      ),
      shouldRender: amortizacionesPrestamosById?.some(
        (amortizacion: AmortizacionReadingDtoProps) =>
          amortizacion?.comisiones?.some(
            (comision) => comision.tipoComision === TIPO_COMISION.OTRA
          )
      ),
    },
    {
      key: "totalInteres",
      header: t("prestamos.idView.tab.headerTax"),
      field: "totalInteres",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(rowData.totalInteres)}
        </span>
      ),
      shouldRender: amortizacionesPrestamosById?.some(
        (amortizacion: AmortizacionReadingDtoProps) =>
          amortizacion?.totalInteres > 0
      ),
    },
    {
      key: "importe",
      header: t("prestamos.idView.tab.headerWhitteOff"),
      field: "importe",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(rowData.importe)}
        </span>
      ),
    },
    {
      key: "capitalVivo",
      header: t("prestamos.idView.tab.headerOutstandingPrincipal"),
      field: "capitalVivo",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => (
        <span
          className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
            isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
          }`}
        >
          {formatCurrency(rowData.capitalVivo)}
        </span>
      ),
    },
    {
      key: "flujo",
      header: t("prestamos.idView.tab.headerFlow"),
      field: "flujo",
      alignCol: "right",
      className: "!tw-text-right !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => {
        const simbol = rowData.flujo < 0
        return (
          <span
            className={`tw-p-3 !tw-h-full tw-flex tw-items-center tw-justify-end ${
              isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
            } ${
              simbol ? "tw-text-danger-20" : "tw-text-secondary-75"
            } tw-font-medium`}
          >
            {formatCurrency(rowData.flujo)}
          </span>
        )
      },
    },
    {
      key: "asientoContable",
      header: t("prestamos.idView.tab.headerEntry"),
      field: "asientoContable",
      className: "!tw-text-center !tw-p-0 !tw-h-12",
      alignCol: "center",
      body: (rowData: AmortizacionReadingDtoProps) => {
        const isChecked = Boolean(rowData?.asientoContable)
        return (
          <span
            className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-p-3 ${
              isDatePastOrToday(rowData) ? "!tw-bg-danger-10" : ""
            }`}
          >
            <InputSwitch
              checked={isChecked}
              onChange={() => {
                rowData.id &&
                  actions.handleEditAmortizacion(rowData.id, !isChecked)
              }}
              disabled={Boolean(!rowData?.id)}
            />
          </span>
        )
      },
    },
    {
      key: "anotation",
      className: "!tw-text-center !tw-p-0 !tw-h-12",
      body: (rowData: AmortizacionReadingDtoProps) => {
        return (
          <span
            className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-p-3 tw-gap-2 ${
              isDatePastOrToday(rowData) ? "!tw-bg-danger-10 " : ""
            }`}
          >
            {IconAnnotationTemplate(
              rowData,
              Boolean(rowData.nota),
              isAnnotation(rowData),
              () => actions.handleOpenAnnotationModal(rowData)
            )}
            {isExpansionLoan(rowData) ? (
              <FontAwesomeIcon
                onClick={() => {
                  actions.setShowDelete({
                    show: true,
                    title: `${t("prestamos.idView.deleteButton")}${t(
                      "prestamos.idView.deteleAmortizacion"
                    )}`,
                    text: "¿Estás seguro?",
                    mode: TYPE_DELETE_PRESTAMOS.amortizaciones,
                    id: rowData.id,
                  })
                }}
                icon={faTrashAlt}
                className='tw-cursor-pointer tw-font-bold tw-h-5 tw-w-5 tw-text-danger'
              />
            ) : (
              <div className='tw-h-5 tw-w-5' />
            )}
          </span>
        )
      },
    },
  ]

  return columns.filter(
    (column) => column.shouldRender === undefined || column.shouldRender
  )
}
